/**
 * 새로고침·재기동으로 수업 화면(/live)을 새 문서로 열었을 때 돌아갈 로그인 페이지 경로를 쿠키로 보관한다. (R-126, Q-019)
 *
 * - 경로는 RabbitmqClient 의 현재 페이지 종류(currentPageType)와 연결 끊김 시 이동 규칙(resolveNavigationTarget)으로 정한다.
 *   RabbitmqClient 가 페이지 종류를 정할 때마다 저장한다.
 * - WebLivePage 는 로그인 상태가 없으면 /main 대신 이 경로로 보낸다. 값이 없거나 허용 목록 밖이면 /main.
 * - 허용 목록 밖의 값(외부 주소·임의 경로)은 쓰지 않는다(open redirect 방지).
 * - 저장은 reducer 밖(microtask)에서 한다. 로그인 성공 reducer 안에서는 store 를 읽을 수 없고, PBL 모드 등은
 *   같은 함수 뒤쪽에서 정해지기 때문이다. 연속 호출이면 마지막 호출만 저장한다. (32차, R-127)
 * - 로그인 세션 밖(로그인 페이지·새 문서)에서는 쿠키의 사용자 쿼리(userID·userNickname)를 현재 페이지가 받은 쿼리로 정한다.
 *   PBL 은 PBL 앱이 쿼리로 넘긴 사용자 정보를 유지하고, 조용한 재로그인 쿼리가 없는 페이지(직접 나간 뒤 등)는 쿼리 없이 둔다.
 *   store 가 비어 계산된 `userID=undefined` 같은 값은 저장하지 않는다.
 *   실제 페이지 이동(resolveNavigationTarget·window.location)은 바꾸지 않고 쿠키 값만 정한다. (35차, R-134)
 *
 * @author hjkim.
 * @since 2026-10-20.
 */

const COOKIE_NAME = "loginRecoveryPath";
const EXPIRE_HOURS = 3; // 20261020 liveSeq 등 수업 쿠키(PerformLiveContainer onHandleCookies)와 같은 만료

// 20261020 R-127: 예약 저장 순번(마지막 호출·직접 저장 우선)
let scheduleSeq = 0;

// 20261020 resolveNavigationTarget 이 돌려주는 경로만 허용한다.
const ALLOWED_PATTERNS = [
    /^\/main$/,
    /^\/class-monitor\/login$/,
    /^\/(main-qrLogin|login|pbl-login|smarttv-login|group-smarttv-login)\/-?\d+$/,
    /^\/(mixed-login|mixed_login|pbl-mixed-login)\/\d+$/,
];

/**
 * 허용된 복귀 경로인지 검사한다. 쿼리는 허용하되 경로 부분만 허용 목록과 비교한다.
 *
 * @param {String} value - 경로(쿼리 포함 가능)
 * @returns {Boolean}
 * @since 2026-10-20.
 */
export const isAllowedRecoveryPath = (value) => {
    if (typeof value !== "string" || value.length === 0 || value.length > 512) return false;
    if (!value.startsWith("/") || value.startsWith("//") || value.indexOf("\\") >= 0) return false;
    const qIndex = value.indexOf("?");
    const pathname = qIndex >= 0 ? value.slice(0, qIndex) : value;
    return ALLOWED_PATTERNS.some(re => re.test(pathname));
};

// 20261020 R-134: 쿼리의 사용자 ID 가 실제 값인지(store 가 비어 붙은 "undefined"·"null"·빈 값 제외)
const isUsableUserID = (v) => typeof v === "string" && v !== "" && v !== "undefined" && v !== "null";

/**
 * 로그인 세션 밖에서 저장할 경로를 정한다. (R-134)
 *
 * - 경로 부분은 그대로 두고, 사용자 쿼리는 현재 페이지가 같은 경로이면서 쿼리로 받은 userID 가 있을 때만 그 값을 쓴다.
 *   (PBL 앱이 넘긴 사용자 정보, 연결 끊김 후 이동한 조용한 재로그인 쿼리는 유지된다.)
 * - 그 밖에는 쿼리 없이 저장한다(직접 나간 뒤의 로그인 페이지, store 가 비어 계산된 undefined 등).
 *
 * @param {String} target - resolveNavigationTarget 결과
 * @returns {String}
 * @since 2026-10-20.
 */
export const resolveOutOfSessionPath = (target) => {
    if (typeof target !== "string") return target;
    const qIndex = target.indexOf("?");
    const pathname = qIndex >= 0 ? target.slice(0, qIndex) : target;
    try {
        if (window.location && window.location.pathname === pathname) {
            const params = new URLSearchParams(window.location.search || "");
            const userID = params.get("userID");
            if (isUsableUserID(userID)) {
                const nick = params.get("userNickname") || "";
                return `${pathname}?userID=${encodeURIComponent(userID)}&userNickname=${encodeURIComponent(nick)}`;
            }
        }
    } catch (e) {
        console.log("resolveOutOfSessionPath - 현재 페이지 쿼리 읽기 실패", e);
    }
    return pathname;
};

/**
 * 복귀 경로를 쿠키에 저장한다. 허용 목록 밖이면 저장하지 않는다.
 *
 * @param {String} value - resolveNavigationTarget 결과
 * @since 2026-10-20.
 */
export const saveLoginRecoveryPath = (value) => {
    // 20261020 R-127: 직접 저장(의도 종료 등)은 아직 실행되지 않은 예약 저장보다 우선한다.
    scheduleSeq++;
    try {
        if (!isAllowedRecoveryPath(value)) return;
        const expires = new Date(Date.now() + EXPIRE_HOURS * 60 * 60 * 1000).toUTCString();
        document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; path=/; expires=${expires}; SameSite=Lax`;
    } catch (e) {
        console.log("saveLoginRecoveryPath - 저장 실패", e);
    }
};

/**
 * 복귀 경로 계산·저장을 현재 동기 처리(reducer 포함)가 끝난 뒤로 미룬다. 마지막 호출만 저장한다. (R-127)
 *
 * 홈 페이지가 아닌데 결과가 /main 이면(예: mixed 로그인 페이지가 처음 열려 아직 mixedClassSeq 를 모를 때) 저장하지 않는다.
 * 이전에 저장된 올바른 경로를 지우지 않기 위해서다. (R-133)
 *
 * 로그인 세션 밖이면(isInSession() 이 true 가 아니면) 사용자 쿼리는 현재 페이지가 받은 쿼리로 정한다(resolveOutOfSessionPath).
 * 수업 중 저장은 계산 결과(사고 복구용 쿼리 포함)를 그대로 쓴다. (R-134)
 *
 * @param {Function} resolveFn - 경로를 계산하는 함수(RabbitmqClient 의 연결 끊김 이동 규칙)
 * @param {Boolean} [allowMain=true] - /main 결과를 저장할지(홈 페이지일 때만 true)
 * @param {Function} [isInSession] - 저장 시점에 로그인 세션 안인지. 없으면 기존처럼 계산 결과 그대로
 * @author hjkim.
 * @since 2026-10-20.
 */
export const scheduleLoginRecoveryPath = (resolveFn, allowMain = true, isInSession = null) => {
    const seq = ++scheduleSeq;
    Promise.resolve().then(() => {
        if (seq !== scheduleSeq) return;
        try {
            const target = resolveFn();
            if (!target || target === "") return;
            if (target === "/main" && !allowMain) return; // 20261020 R-133
            let inSession = true;
            if (typeof isInSession === "function") {
                try { inSession = isInSession() === true; } catch (e) { inSession = false; }
            }
            saveLoginRecoveryPath(inSession ? target : resolveOutOfSessionPath(target)); // 20261020 R-134
        } catch (e) {
            console.log("scheduleLoginRecoveryPath - 실패", e);
        }
    });
};

/**
 * 저장된 복귀 경로(허용 목록 안의 값만). 없으면 null.
 *
 * @returns {String|null}
 * @since 2026-10-20.
 */
export const readLoginRecoveryPath = () => {
    try {
        const parts = (document.cookie || "").split(";");
        for (let i = 0; i < parts.length; i++) {
            const p = parts[i].trim();
            if (p.indexOf(`${COOKIE_NAME}=`) === 0) {
                const value = decodeURIComponent(p.slice(COOKIE_NAME.length + 1));
                return isAllowedRecoveryPath(value) ? value : null;
            }
        }
    } catch (e) {
        console.log("readLoginRecoveryPath - 읽기 실패", e);
    }
    return null;
};

/**
 * 로그인 상태 없이 /live 가 열렸을 때 보낼 경로. 저장값이 없으면 기존처럼 /main.
 *
 * @returns {String}
 * @since 2026-10-20.
 */
export const getLiveReloadRedirectTarget = () => readLoginRecoveryPath() || "/main";
